// The group new-joiner welcome dialog.
//
// One scrollable pass over everything a new member needs: the owner's greeting, what the group is
// about, its rules, its topics, and its resources — with a single confident way out. Design:
// designs/01-now/2026-07-21-group-new-joiner-welcome-ux-design.md (UX) and
// designs/01-now/2026-07-21-group-new-joiner-welcome-technical-design.md (§6).
//
// Purely presentational: it takes fully-resolved content and renders it. The decision of WHETHER to
// show it (the suppression rule) and WHEN (the join / approval triggers) lives in
// openGroupWelcomeDialog.tsx, so this component stays trivially previewable from the component
// showcase and from the owner's "Preview welcome" action.

import React from "react";
import { ArrowUpRight, ChevronDown, ExternalLink } from "lucide-react";
import SimpleDialog, { SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";
import type { GroupResourceLike, GroupRuleLike } from "@/main/react/group/GroupGovernanceTypes";
import { ugcAnchorRel } from "@/main/common/ugcAnchorRel";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { DiscussionWithoutContentDTO } from "@/generated-client";

/** "join" greets a brand-new member; "about" is the on-demand re-read (and the non-member preview). */
export type GroupWelcomeDialogMode = "join" | "about";

/** How many rules/resources are shown inline before we hand off to the group's own surfaces. */
export const WELCOME_RULES_INLINE_LIMIT = 5;
export const WELCOME_RESOURCES_INLINE_LIMIT = 4;

export type GroupWelcomeContent = {
    groupName: string;
    groupSlug?: string | null;
    description?: string | null;
    bannerUrl?: string | null;
    thumbnailUrl?: string | null;
    /** Display name of the group owner, used only to attribute the greeting. */
    ownerName?: string | null;
    welcomeMessage?: string | null;
    rules?: GroupRuleLike[];
    resources?: GroupResourceLike[];
    topics?: string[];
    /**
     * `GROUP-81` (U27 / C65, mega solo `mr2-group`): when present on a JOIN greeting, the dialog looks up
     * the group's newest post and offers "Read the newest post" as its primary action, so the dialog that
     * orients a new member leads somewhere inside the group. Absent (the owner's preview, the About
     * dialog) the footer is exactly the one dismiss button it always was.
     */
    groupPublicId?: string | null;
};

type GroupWelcomeDialogProps = GroupWelcomeContent & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: GroupWelcomeDialogMode;
    localizer: Localizer;
};

/**
 * `GROUP-133` (V17 a): where the body's visible area should END so it lands on a section boundary.
 *
 * <p>`sections` are the body's `<section>` boxes in scroll-content coordinates (top of the scroll content = 0),
 * `viewportHeight` the scroller's unclamped client height. Returns the height to cap the scroller at (the top
 * of the first section that straddles the fold), or null when nothing straddles it or when the cap would
 * cost more than half the viewport (a long greeting is better cut than a near-empty dialog).
 */
export function welcomeSectionBoundaryCap(
    viewportHeight: number,
    sections: ReadonlyArray<{ top: number; bottom: number }>,
): number | null {
    if (!(viewportHeight > 0)) {
        return null;
    }
    const straddling = sections.find((section) => section.top < viewportHeight && section.bottom > viewportHeight);
    if (!straddling || straddling.top < viewportHeight / 2) {
        return null;
    }
    return Math.floor(straddling.top);
}

function SectionHeading({ children }: { children: React.ReactNode }) {
    return (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>
    );
}

/**
 * A rule: title always visible, longer description collapsed underneath and expandable in place.
 * This is the compromise that keeps a 7-rule group from becoming a wall of text while still leaving
 * every rule one tap from readable.
 */
function WelcomeRule({ rule, index, localizer }: { rule: GroupRuleLike; index: number; localizer: Localizer }) {
    const [expanded, setExpanded] = React.useState(false);
    const description = rule.description?.trim();
    const title = rule.title?.trim();
    if (!title) {
        return null;
    }

    const numberedTitle = (
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
            <span className="mr-1.5 text-muted-foreground">{index + 1}.</span>
            {title}
        </span>
    );

    if (!description) {
        return (
            <li className="flex gap-1 rounded-2xl border border-border bg-muted px-3 py-2">
                {numberedTitle}
            </li>
        );
    }

    return (
        <li className="rounded-2xl border border-border bg-muted">
            <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
                // `GROUP-135` (A23): `items-center`, so the 44 px touch floor centres a one-line title and its
                // chevron instead of leaving them ~4 px above centre with a dead band below. The chevron's
                // old `mt-0.5` first-line nudge went with `items-start`.
                className="flex w-full items-center gap-1 px-3 py-2 text-left [@media(pointer:coarse)]:min-h-11"
                data-playwright-id="group-welcome-rule-toggle"
            >
                {numberedTitle}
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        "size-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
                        expanded ? "rotate-180" : undefined
                    )}
                />
                {/* `GROUP-122` (A40): no sr-only copy of the title here. `numberedTitle` above already
                    names the toggle, so the duplicate made every rule announce its title twice. */}
            </button>
            {expanded ? (
                <p className="px-3 pb-2.5 text-sm text-muted-foreground">{description}</p>
            ) : null}
        </li>
    );
}

function WelcomeResource({ resource }: { resource: GroupResourceLike }) {
    const title = resource.title?.trim();
    const url = resource.url?.trim();
    if (!title || !url) {
        return null;
    }

    return (
        <li>
            <a
                href={url}
                // New tab on purpose: a resource link should never cost the user the group they just joined.
                target="_blank"
                rel={ugcAnchorRel(url)}
                className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground hover:bg-accent [@media(pointer:coarse)]:min-h-11"
                data-playwright-id="group-welcome-resource"
            >
                <span className="min-w-0 flex-1 truncate">{title}</span>
                <ExternalLink aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
            </a>
        </li>
    );
}

export function GroupWelcomeDialog({
    open,
    onOpenChange,
    mode,
    localizer,
    groupName,
    groupSlug,
    description,
    bannerUrl,
    thumbnailUrl,
    ownerName,
    welcomeMessage,
    rules,
    resources,
    topics,
    groupPublicId,
}: GroupWelcomeDialogProps) {
    const bodyRef = React.useRef<HTMLDivElement | null>(null);

    // Focus the dialog body rather than the primary button, so a screen reader reads the owner's
    // greeting instead of announcing "Start exploring" first (UX §10).
    React.useEffect(() => {
        if (!open) {
            return;
        }
        const frame = window.requestAnimationFrame(() => bodyRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [open]);

    /*
     * GROUP-41 (chat completeness `1h`, 2026-09-27): the scroll container is SimpleDialog's body wrapper
     * (`overflow-y-auto no-scrollbar`), so overflow had no cue at all and on a shorter phone the
     * RESOURCES section was simply invisible. While the body is scrolled short of its end, a fade at the
     * bottom edge says there is more; at the end it goes. Measured on the member's scroll and on resize,
     * never guessed from the content count.
     */
    const [moreBelow, setMoreBelow] = React.useState(false);
    // The body node as STATE as well as a ref: Radix mounts the dialog content after `open` flips, so an
    // effect keyed on `open` alone can run before the node exists and never run again.
    const [bodyNode, setBodyNode] = React.useState<HTMLDivElement | null>(null);
    // Stable identity: an inline ref callback is detached (null) and re-attached on EVERY render, which
    // would write this state twice per render and never settle.
    const attachBody = React.useCallback((node: HTMLDivElement | null) => {
        bodyRef.current = node;
        setBodyNode(node);
    }, []);
    React.useEffect(() => {
        if (!open || !bodyNode) {
            return;
        }
        const scroller = bodyNode.parentElement;
        if (!scroller) {
            return;
        }
        const measure = () => {
            setMoreBelow(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 4);
        };
        measure();
        scroller.addEventListener("scroll", measure, { passive: true });
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
        observer?.observe(scroller);
        observer?.observe(bodyNode);
        return () => {
            scroller.removeEventListener("scroll", measure);
            observer?.disconnect();
        };
    }, [open, bodyNode]);

    /*
     * `GROUP-133` (V17 a): the GROUP-41 fade used to lie over whichever section the fold happened to cut, so
     * at rest a heading (TOPICS, on the filed frames) rendered washed out and read as disabled. The fade
     * stays; the visible area now ENDS ON A SECTION BOUNDARY: once laid out, the scroller is capped at the top
     * of the first section that straddles the fold, so what the fade overlays is the gap and the tail of a
     * complete section, never a half-shown heading. Measured with `offsetTop`, which the dialog's open
     * transform (a scale) does not distort, and re-measured on a window resize from the uncapped height.
     */
    React.useEffect(() => {
        if (!open || !bodyNode) {
            return;
        }
        const scroller = bodyNode.parentElement;
        if (!scroller) {
            return;
        }
        const applyCap = () => {
            scroller.style.maxHeight = "";
            const sections = (Array.from(bodyNode.children) as HTMLElement[])
                .filter((child) => child.tagName === "SECTION" && child.offsetParent === scroller.offsetParent)
                .map((section) => {
                    const top = section.offsetTop - scroller.offsetTop;
                    return { top, bottom: top + section.offsetHeight };
                });
            const cap = welcomeSectionBoundaryCap(scroller.clientHeight, sections);
            if (cap !== null) {
                scroller.style.maxHeight = `${cap}px`;
            }
        };
        const frame = window.requestAnimationFrame(applyCap);
        window.addEventListener("resize", applyCap);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener("resize", applyCap);
            scroller.style.maxHeight = "";
        };
    }, [open, bodyNode]);

    // `GROUP-81`: the newest post's address, looked up once per open of a JOIN greeting. A failed or empty
    // lookup leaves the footer as it was; the dialog never waits on it.
    const [newestPostHref, setNewestPostHref] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (!open || mode !== "join" || !groupPublicId) {
            return;
        }
        let active = true;
        ClientFactory.getGroupDiscussions(groupPublicId)
            .then((response) => {
                if (!active) return;
                const newest = ((response?.data ?? []) as DiscussionWithoutContentDTO[])
                    .find((discussion) => !discussion.deleted && Boolean(discussion.slug?.trim() || discussion.publicId?.trim()));
                const target = newest ? (newest.slug?.trim() || newest.publicId?.trim()) : "";
                setNewestPostHref(target ? `/discussion/${encodeURIComponent(target)}` : null);
            })
            .catch(() => {
                // Best effort: the greeting is complete without it.
            });
        return () => {
            active = false;
        };
    }, [open, mode, groupPublicId]);

    const trimmedWelcome = welcomeMessage?.trim();
    const trimmedDescription = description?.trim();
    const visibleRules = (rules ?? []).filter((rule) => rule.title?.trim());
    const visibleResources = (resources ?? []).filter((resource) => resource.title?.trim() && resource.url?.trim());
    const visibleTopics = (topics ?? []).filter((topic) => topic?.trim());

    const inlineRules = visibleRules.slice(0, WELCOME_RULES_INLINE_LIMIT);
    const inlineResources = visibleResources.slice(0, WELCOME_RESOURCES_INLINE_LIMIT);
    const headerImage = bannerUrl?.trim() || thumbnailUrl?.trim() || null;

    const title = mode === "join" ? localizer("group.welcome.title_join", groupName) : groupName;
    const primaryLabel =
        mode === "join" ? localizer("group.welcome.action.start_exploring") : localizer("group.welcome.action.close");

    return (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<span data-playwright-id="group-welcome-title">{title}</span>}
            width="lg"
        >
            {/*
              * `gap-4`, and a shorter header image, to pay for the rule rows' tap-target floor.
              *
              * Giving each expandable rule row `min-h-11` on a coarse pointer is correct and stays —
              * a full-width row that responds to a tap must be tappable. But it cost 8px per row over
              * four rows, and that is what pushed the RESOURCES heading and its first row past the
              * fold of a 412x839 dialog. The floor is not the thing to give back: the budget came out
              * of decoration and rhythm instead — 4px x 5 sections from the body gap, 16px from a
              * purely decorative image — which is ~36px against the 32px the floor took.
              *
              * `touch-hit-44` is NOT the alternative here, and it is the tempting wrong answer: it
              * centres a 44x44 pseudo-element, so on a ~350px-wide row it would REPLACE a full-width
              * hit area with a 44px one in the middle. It is for small square controls; its own
              * docstring says to prefer the `min-h-11` floor everywhere else.
              */}
            <div
                ref={attachBody}
                tabIndex={-1}
                className="flex flex-col gap-4 outline-none"
                data-playwright-id="group-welcome-dialog"
            >
                {headerImage ? (
                    // Decorative: the title already names the group, so this adds nothing for AT.
                    <img
                        src={headerImage}
                        alt=""
                        aria-hidden="true"
                        className="h-20 w-full rounded-2xl border border-border object-cover"
                    />
                ) : null}

                {trimmedWelcome ? (
                    <section className="flex flex-col gap-1.5" data-playwright-id="group-welcome-message">
                        {/* The emotional centerpiece — warmer and larger than the body sections, and
                            whitespace-pre-line because owners write greetings with line breaks. */}
                        <p className="whitespace-pre-line text-base leading-relaxed text-foreground">{trimmedWelcome}</p>
                        {ownerName ? (
                            <p className="text-sm text-muted-foreground">{localizer("group.welcome.attribution", ownerName)}</p>
                        ) : null}
                    </section>
                ) : null}

                {trimmedDescription ? (
                    <section className="flex flex-col gap-1.5">
                        <SectionHeading>{localizer("group.welcome.section.about")}</SectionHeading>
                        <p className="line-clamp-4 text-sm text-muted-foreground">{trimmedDescription}</p>
                    </section>
                ) : null}

                {/* GROUP-43: ONE unconditional way through to the group's own About page. The two
                    overflow links below render only when rules or resources overflow, so a small group
                    (every new group) had no route there at all, under a description clamped at four
                    lines. Quiet link styling, so it never competes with the primary action. */}
                {groupSlug ? (
                    <a
                        href={`/group/${groupSlug}/about`}
                        className="self-start text-sm font-medium text-primary hover:underline"
                        data-playwright-id="group-welcome-about-link"
                    >
                        {localizer("group.about.title")}
                    </a>
                ) : null}

                {inlineRules.length > 0 ? (
                    <section className="flex flex-col gap-1.5" data-playwright-id="group-welcome-rules">
                        <SectionHeading>{localizer("group.welcome.section.rules")}</SectionHeading>
                        <ul className="flex flex-col gap-1.5">
                            {inlineRules.map((rule, index) => (
                                <WelcomeRule
                                    key={rule.id ?? `${rule.title}-${index}`}
                                    rule={rule}
                                    index={index}
                                    localizer={localizer}
                                />
                            ))}
                        </ul>
                        {visibleRules.length > WELCOME_RULES_INLINE_LIMIT && groupSlug ? (
                            <a
                                href={`/group/${groupSlug}/about`}
                                className="text-sm font-medium text-primary hover:underline"
                                data-playwright-id="group-welcome-rules-overflow"
                            >
                                {localizer("group.welcome.rules_overflow", String(visibleRules.length))}
                            </a>
                        ) : null}
                    </section>
                ) : null}

                {visibleTopics.length > 0 ? (
                    <section className="flex flex-col gap-1.5">
                        <SectionHeading>{localizer("group.welcome.section.topics")}</SectionHeading>
                        <div className="flex flex-wrap gap-1.5">
                            {visibleTopics.map((topic) => (
                                <a
                                    key={topic}
                                    href={`/tag/${encodeURIComponent(topic)}`}
                                    // GROUP-42 (operator, 2026-09-27, option A): still the site-wide tag
                                    // feed, but it LOOKS like a link (primary ink + arrow) and opens in a
                                    // new tab, so the greeting survives, the same rule the resource links
                                    // above follow. It used to close the dialog and navigate away while
                                    // styled as an inert grey label. `ArrowUpRight` is already on the
                                    // eager graph, so it costs no bundle budget.
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="relative touch-hit-44 inline-flex items-center gap-0.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-primary hover:bg-accent hover:underline"
                                    data-playwright-id="group-welcome-topic"
                                >
                                    #{topic}
                                    <ArrowUpRight aria-hidden="true" className="size-3 shrink-0" />
                                </a>
                            ))}
                        </div>
                    </section>
                ) : null}

                {inlineResources.length > 0 ? (
                    <section className="flex flex-col gap-1.5" data-playwright-id="group-welcome-resources">
                        <SectionHeading>{localizer("group.welcome.section.resources")}</SectionHeading>
                        <ul className="flex flex-col gap-1.5">
                            {inlineResources.map((resource, index) => (
                                <WelcomeResource
                                    key={resource.id ?? `${resource.title}-${index}`}
                                    resource={resource}
                                />
                            ))}
                        </ul>
                        {visibleResources.length > WELCOME_RESOURCES_INLINE_LIMIT && groupSlug ? (
                            <a
                                href={`/group/${groupSlug}/about`}
                                className="text-sm font-medium text-primary hover:underline"
                                data-playwright-id="group-welcome-resources-overflow"
                            >
                                {localizer("group.welcome.resources_overflow")}
                            </a>
                        ) : null}
                    </section>
                ) : null}

                {moreBelow ? (
                    // Sticky inside the body, so it rides the bottom edge of the scroll viewport. `-mt-12`
                    // cancels its own height plus the body's `gap-4`, so it overlays content rather than
                    // adding space. Decorative: the content it hints at is reachable by scrolling.
                    <div
                        aria-hidden="true"
                        className="pointer-events-none sticky bottom-0 -mt-12 h-8 shrink-0 bg-gradient-to-t from-popover to-transparent"
                        data-playwright-id="group-welcome-more-cue"
                    />
                ) : null}
            </div>

            <SimpleDialogFooter>
                {/* The dismiss keeps its `group-welcome-primary-action` hook (the join specs close the
                    dialog through it); it steps down to the secondary tier only when "Read the newest
                    post" exists to lead. Primary last. */}
                <Button
                    type="button"
                    variant={newestPostHref ? "ghost" : "default"}
                    className="w-full [@media(pointer:coarse)]:min-h-11 sm:w-auto"
                    onClick={() => onOpenChange(false)}
                    data-playwright-id="group-welcome-primary-action"
                >
                    {primaryLabel}
                </Button>
                {newestPostHref ? (
                    <Button asChild className="w-full [@media(pointer:coarse)]:min-h-11 sm:w-auto">
                        <a href={newestPostHref} data-playwright-id="group-welcome-newest-post">
                            {localizer("group.welcome.action.read_newest_post")}
                        </a>
                    </Button>
                ) : null}
            </SimpleDialogFooter>
        </SimpleDialog>
    );
}

export default GroupWelcomeDialog;
